import { t } from '@trimble/i18n';
import { ViewMode } from '@mobile-mapping/web3d';
import { Accessor, Component, createMemo, Match, Switch } from 'solid-js';
import { Empty } from '@tcweb/connect-ui';
import { VIEW_MODE_ICONS } from '../view-mode-picker/view-mode-icons';

export interface EmptyViewerStateProps {
  viewMode: Accessor<ViewMode>;
  cameraIndex: Accessor<number | null>;
  /**
   * csib of the currently loaded dataset. `undefined` indicates the empty-2D
   * state. Sourced from the load-flow signal (DataContext.currentLoadedCsib).
   */
  datasetCsib: Accessor<string | undefined>;
  pcProvider: Accessor<{ items(): unknown[] } | undefined>;
  trbProvider: Accessor<{ items(): unknown[] } | undefined>;
  imagePointcloudCap: Accessor<{ isActive(): boolean } | undefined>;
  imageCaps: Accessor<
    | {
        capabilities: {
          selectedStationIndex?(): number | null;
          isCurrentStationImageReady?(): boolean;
          cameraName?: string;
        }[];
      }
    | undefined
  >;
  stationsProvider: Accessor<
    { items(): { cameraName: string; stationList: unknown[] }[] } | undefined
  >;
  backgroundTopColor: Accessor<string>;
  backgroundBottomColor: Accessor<string>;
  class?: string;
}

type PaneContentState =
  | 'empty2D'
  | 'empty3D'
  | 'emptyImageNoStationSelected'
  | 'emptyImageNoImageAtStation'
  | 'emptyImageNoImageData'
  | 'emptyImageNotReady'
  | 'emptyPanoramaNoStationSelected'
  | 'emptyPanoramaNoImageAtStation'
  | 'emptyPanoramaNoImageData'
  | 'emptyPanoramaNotReady'
  | 'hasContent';

export const EmptyViewerState: Component<EmptyViewerStateProps> = (props) => {
  const backgroundProps = (): {
    backgroundTopColor: string;
    backgroundBottomColor: string;
  } => ({
    backgroundTopColor: props.backgroundTopColor(),
    backgroundBottomColor: props.backgroundBottomColor(),
  });

  const state = createMemo<PaneContentState>(() => {
    const vm = props.viewMode();

    if (vm.type === '2D') {
      return props.datasetCsib() === undefined ? 'empty2D' : 'hasContent';
    }

    if (vm.type === '3D') {
      const pcProvider = props.pcProvider();
      const trbProvider = props.trbProvider();
      const hasPointClouds = pcProvider ? pcProvider.items().length > 0 : false;
      const hasModels = trbProvider ? trbProvider.items().length > 0 : false;
      return !hasPointClouds && !hasModels ? 'empty3D' : 'hasContent';
    }

    const isPanorama = vm.type === 'Panorama';
    const imageCaps = props.imageCaps();
    const cameraIndex = isPanorama ? 0 : (props.cameraIndex() ?? -1);
    const activeImageCap = imageCaps?.capabilities[cameraIndex];
    const stationsP = props.stationsProvider();

    const cameraStations = stationsP
      ?.items()
      .find((s) => s.cameraName === activeImageCap?.cameraName);

    if (!cameraStations || cameraStations.stationList.length === 0) {
      return isPanorama ? 'emptyPanoramaNoImageData' : 'emptyImageNoImageData';
    }

    const imagePointcloudCap = props.imagePointcloudCap();
    if (imagePointcloudCap?.isActive()) {
      return 'hasContent';
    }

    const selectedStationIdx = activeImageCap?.selectedStationIndex?.();

    if (selectedStationIdx == null) {
      return isPanorama
        ? 'emptyPanoramaNoStationSelected'
        : 'emptyImageNoStationSelected';
    }

    const stationData = cameraStations.stationList[selectedStationIdx];
    if (!stationData) {
      return isPanorama
        ? 'emptyPanoramaNoImageAtStation'
        : 'emptyImageNoImageAtStation';
    }

    if (activeImageCap?.isCurrentStationImageReady?.() === false) {
      return isPanorama ? 'emptyPanoramaNotReady' : 'emptyImageNotReady';
    }

    return 'hasContent';
  });

  return (
    <Switch>
      <Match when={state() === 'empty2D'}>
        <Empty
          icon={VIEW_MODE_ICONS['2D']}
          message={t('mobileMapping.emptyPaneStates.no2DData')}
          data-testid="empty-2d-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>

      <Match when={state() === 'empty3D'}>
        <Empty
          icon={VIEW_MODE_ICONS['3D']}
          message={t('mobileMapping.emptyPaneStates.no3DData')}
          data-testid="empty-3d-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>

      <Match when={state() === 'emptyImageNoStationSelected'}>
        <Empty
          icon={VIEW_MODE_ICONS['Image']}
          message={t('mobileMapping.emptyPaneStates.noStationSelected')}
          data-testid="empty-image-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>

      <Match when={state() === 'emptyImageNoImageAtStation'}>
        <Empty
          icon={VIEW_MODE_ICONS['Image']}
          message={t('mobileMapping.emptyPaneStates.noImageAtStation')}
          data-testid="empty-image-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>

      <Match when={state() === 'emptyImageNoImageData'}>
        <Empty
          icon={VIEW_MODE_ICONS['Image']}
          message={t('mobileMapping.emptyPaneStates.noImageData')}
          data-testid="empty-image-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>

      <Match when={state() === 'emptyImageNotReady'}>
        <Empty
          icon={VIEW_MODE_ICONS['Image']}
          message={t('mobileMapping.emptyPaneStates.imageNotReady')}
          data-testid="empty-image-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>

      <Match when={state() === 'emptyPanoramaNotReady'}>
        <Empty
          icon={VIEW_MODE_ICONS['Panorama']}
          message={t('mobileMapping.emptyPaneStates.panoramaNotReady')}
          data-testid="empty-panorama-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>

      <Match when={state() === 'emptyPanoramaNoStationSelected'}>
        <Empty
          icon={VIEW_MODE_ICONS['Panorama']}
          message={t('mobileMapping.emptyPaneStates.noPanoramaStationSelected')}
          data-testid="empty-panorama-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>

      <Match when={state() === 'emptyPanoramaNoImageAtStation'}>
        <Empty
          icon={VIEW_MODE_ICONS['Panorama']}
          message={t('mobileMapping.emptyPaneStates.noPanoramaAtStation')}
          data-testid="empty-panorama-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>

      <Match when={state() === 'emptyPanoramaNoImageData'}>
        <Empty
          icon={VIEW_MODE_ICONS['Panorama']}
          message={t('mobileMapping.emptyPaneStates.noPanoramaData')}
          data-testid="empty-panorama-pane"
          class={props.class}
          {...backgroundProps()}
        />
      </Match>
    </Switch>
  );
};
